export const TIMEZONE_HEADER_NAME = 'X-Client-Timezone';
export const TIMEZONE_COOKIE_NAME = 'timezone';

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;
const COOKIE_DOMAIN = import.meta.env.VITE_COOKIE_DOMAIN || '';

/**
 * Parses the client time zone from a cookie header.
 *
 * @param cookieHeader - Raw Cookie header value.
 *
 * @returns IANA time zone string when present; otherwise null.
 */
export const parseTimezoneCookie = (
  cookieHeader: string | null | undefined,
): string | null => {
  if (!cookieHeader) {
    return null;
  }

  const cookies = cookieHeader.split(';');
  for (const cookie of cookies) {
    const [rawName, ...rawValue] = cookie.trim().split('=');
    if (rawName !== TIMEZONE_COOKIE_NAME) {
      continue;
    }

    const decodedValue = decodeURIComponent(rawValue.join('='));
    return decodedValue || null;
  }

  return null;
};

/**
 * Checks whether the time zone cookie exists in a cookie header.
 *
 * @param cookieHeader - Raw Cookie header value.
 *
 * @returns True when the time zone cookie is present.
 */
export const hasTimezoneCookie = (
  cookieHeader: string | null | undefined,
): boolean => {
  if (!cookieHeader) {
    return false;
  }

  const cookies = cookieHeader.split(';');
  for (const cookie of cookies) {
    const [rawName] = cookie.trim().split('=');
    if (rawName === TIMEZONE_COOKIE_NAME) {
      return true;
    }
  }

  return false;
};

/**
 * Returns the browser-resolved time zone when available.
 *
 * @returns IANA time zone string from the browser; otherwise null.
 */
export const getBrowserTimezone = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timezone || null;
};

/**
 * Resolves the client time zone with browser preference.
 *
 * @param cookieHeader - Raw Cookie header for SSR fallback.
 *
 * @returns IANA time zone string when available; otherwise null.
 */
export const resolveClientTimezone = (
  cookieHeader?: string | null,
): string | null => {
  const browserTimezone = getBrowserTimezone();
  if (browserTimezone) {
    return browserTimezone;
  }

  return parseTimezoneCookie(cookieHeader);
};

/**
 * Persists the client time zone in a cookie when possible.
 *
 * @param timeZone - IANA time zone string.
 */
export const writeTimezoneCookie = (timezone: string): void => {
  if (typeof document === 'undefined' || !timezone) {
    return;
  }

  if (hasTimezoneCookie(document.cookie)) {
    return;
  }

  const secureAttribute =
    typeof window !== 'undefined' && window.location.protocol === 'https:'
      ? '; Secure'
      : '';
  const domainAttribute = COOKIE_DOMAIN ? `; Domain=${COOKIE_DOMAIN}` : '';
  document.cookie = `${TIMEZONE_COOKIE_NAME}=${timezone}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secureAttribute}${domainAttribute}`;
};
